import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';

import { FilesSearchWidget as FileSearchWidgetType } from 'types/dashboard_widget';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { isNotNullOrUndefined } from 'services/nullable';

import { useCameras } from 'cameras/hooks/useCameras';
import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import { useFilesSearch } from 'files/hooks/useFilesSearch';
import { useModal } from 'hooks/useModal';
import { useStreams } from 'streams/hooks/useStreams';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { Icon } from 'components/Icon/IconDeprecated';
import { CursorPagination } from 'components/Pagination/CursorPagination';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import { FilesWidgetSkeleton } from './Skeleton';
import { useTrackableNames } from 'trackables/hooks';
import styles from './Files.module.scss';

const c = classNames.bind(styles);

export function FilesSearchWidget(widget: FileSearchWidgetType) {
  const { options } = widget;

  const { gateway_id, stream_ids = [] } = useDashboardFilterParams();

  const { start, stop, timezone } = useTimeRangeParams();

  const { open } = useModal();

  const [page, setPage] = React.useState(1);

  const gatewayIdsFilter = gateway_id
    ? Array.from(new Set([gateway_id, ...(options.gateway_ids || [])]))
    : options.gateway_ids;

  const streamIdsFilter = Array.from(
    new Set([...stream_ids, ...(options.stream_ids || [])])
  );

  const trackableListIds = React.useMemo(
    () =>
      options.trackable_list_ids
        ? Array.from(new Set(options.trackable_list_ids.filter(Boolean)))
        : [],
    [options.trackable_list_ids]
  );

  const trackableItemIds = React.useMemo(
    () =>
      options.trackable_item_ids
        ? Array.from(new Set(options.trackable_item_ids.filter(Boolean)))
        : [],
    [options.trackable_item_ids]
  );

  const combinedQuery = React.useMemo(() => {
    const segments: string[] = [];

    if (options.query?.trim()) {
      segments.push(options.query.trim());
    }

    if (trackableItemIds.length) {
      segments.push(trackableItemIds.join(' '));
    } else if (trackableListIds.length) {
      segments.push(trackableListIds.join(' '));
    }

    return segments.join(' ').trim();
  }, [options.query, trackableItemIds, trackableListIds]);

  const { trackable_list_ids, trackable_item_ids, ...validOptions } = options;

  const {
    data: files,
    isFetching,
    isPreviousData,
    isInitialLoading: isLoadingFiles,
    error,
  } = useFilesSearch({
    params: {
      created_ts_since: start,
      created_ts_until: stop,
      page,
      ...validOptions,
      query: combinedQuery || undefined,
      gateway_ids: gatewayIdsFilter,
      stream_ids: streamIdsFilter.length ? streamIdsFilter : undefined,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  React.useEffect(() => {
    if (page !== 1 && error && error.code === 'invalid-pagination-parameters') {
      setPage(1);
    }
  }, [error, page]);

  function handleNextPage() {
    if (!files?.next_page) return;
    setPage(files.next_page);
  }

  function handlePreviousPage() {
    if (!files?.previous_page) return;
    setPage(files.previous_page);
  }

  const streamIds = [
    ...new Set(
      files?.data.map((file) => file.stream_id).filter(isNotNullOrUndefined)
    ),
  ];

  const { data: streams, isInitialLoading: isLoadingStreams } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    enabled: streamIds.length > 0,
  });

  const streamsCamerasMap = streams
    ? Object.fromEntries(
        streams?.data
          .filter(({ camera_id }) => camera_id)
          .map(({ id, camera_id }) => [id, camera_id])
      )
    : {};

  const cameraIds = [
    ...new Set(Object.values(streamsCamerasMap).filter(isNotNullOrUndefined)),
  ];

  const { data: cameras, isInitialLoading: isLoadingCameras } = useCameras({
    params: { camera_ids: cameraIds, limit: cameraIds.length },
    enabled: cameraIds.length > 0,
  });

  const isLoading = isLoadingFiles || isLoadingStreams || isLoadingCameras;

  const hasFiles = files && files.data.length > 0;
  const showLoading =
    isLoading || (isFetching && !hasFiles) || (isFetching && isPreviousData);

  const trackableIds = React.useMemo(() => {
    if (!files?.data) {
      return [];
    }

    const ids = new Set<string>();
    for (const file of files.data) {
      const trackables =
        'trackables' in file && Array.isArray(file.trackables)
          ? file.trackables
          : [];
      for (const id of trackables) {
        if (id) {
          ids.add(id);
        }
      }
    }

    return Array.from(ids);
  }, [files]);

  const trackableNamesQuery = useTrackableNames(trackableIds);
  const trackableItemsLookup = trackableNamesQuery.data?.items;
  const trackableListsLookup = trackableNamesQuery.data?.lists;

  return (
    <Widget widget={widget}>
      <ul className={c('files')}>
        {!hasFiles && !isLoading && !isFetching && (
          <EmptyView>No files for this time period and filters.</EmptyView>
        )}

        {showLoading ? (
          <SkeletonList
            min={hasFiles ? files?.data.length : 5}
            max={hasFiles ? files?.data.length : 5}
            component={<FilesWidgetSkeleton />}
          />
        ) : (
          <>
            {files?.data.map((file) => {
              const cameraId =
                file.stream_id && streamsCamerasMap[file.stream_id];
              const isFromCameraStream = Boolean(file.stream_id && cameraId);
              const source = isFromCameraStream
                ? cameras?.data.find(({ id }) => id === cameraId)
                : streams?.data.find(({ id }) => id === file.stream_id);
              const trackables =
                'trackables' in file && Array.isArray(file.trackables)
                  ? file.trackables
                  : [];

              return (
                <li key={file.file_id}>
                  <button
                    className={c('file', 'border')}
                    onClick={() =>
                      open('files-search', {
                        defaultFile: {
                          id: file.file_id,
                          created_at: file.created_at,
                        },
                        defaultStreamId: file.stream_id,
                      })
                    }
                  >
                    <Thumbnail
                      thumbnail_url={file.thumbnail_url}
                      mime_type={file.mime_type}
                    />
                    <div className={c('info')}>
                      <Text size="small" inline>
                        {DateTime.fromISO(file.created_at)
                          .setZone(timezone)
                          .toLocaleString(DateTime.DATETIME_MED)}
                      </Text>

                      <div className={c('entities')}>
                        {source && (
                          <Text className={c('text')} inline>
                            <Icon
                              name={isFromCameraStream ? 'camera' : 'stream'}
                            />
                            <span className={c('text-inner')}>
                              {source.name}
                            </span>
                          </Text>
                        )}
                      </div>

                      <FileSearchMetadata
                        layout="compact"
                        maxObjectIcons={6}
                        objects={file.objects}
                        events={file.events}
                        texts={file.texts}
                        trackables={trackables}
                        trackableItemsLookup={trackableItemsLookup}
                        trackableListsLookup={trackableListsLookup}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </>
        )}

        {/* Note: Using CursorPagination for UI consistency, even though the underlying
            search API uses offset-based pagination. When the search API adds cursor
            support, this implementation will be updated accordingly. */}
        <CursorPagination
          className={c('pagination')}
          page={page}
          hasNextPage={Boolean(files?.next_page)}
          hasPreviousPage={Boolean(files?.previous_page)}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          disabled={isFetching}
        />
      </ul>
    </Widget>
  );
}
